import { useEffect, useMemo, useState } from 'react';
import { observer } from 'mobx-react-lite';
import { Dropdown, Menu } from 'antd';
import { useTenants } from '../hooks/useTenants';
import SystemsStore from 'Stores/SystemsStore';
import NavItem from './NavItem';
import { NavItemConfig } from '../Siderbar.constant';
import Icon from 'Components/Icon/Icon';
import DebounceInput from '../../DebouncedInput';
import styles from '../Sidebar.module.scss';

type Props = {
    isSuperAdmin: boolean,
    systemsStore: SystemsStore,
    showNoSelection: boolean
}

const tenantIcon = <Icon type="recipientProperty" />

const TenantSelection = ({ isSuperAdmin, systemsStore, showNoSelection }: Props) => {
    const [search, setSearch] = useState("");
    const [isDropdownOpen, setIsDropdownOpen] = useState(false);
    const { loading } = useTenants();
    const { selectedTenant, tenantList } = systemsStore;
    const isCollapsed = localStorage.getItem("menu-collapsed") === "true";

    const handleItemClick = (item: NavItemConfig) => {
        setIsDropdownOpen(false);
        systemsStore.changeTenant({ label: item.label, value: Number(item.path) });
    }

    const TENANTS: NavItemConfig[] = useMemo(() => {
        return tenantList.length ? [{ key: "No Tenant Selected", label: "No Tenant Selected", path: '0', icon: tenantIcon }, ...tenantList.map(tenant => ({
            key: tenant.label,
            label: tenant.label,
            path: `${tenant.value}`,
            icon: tenantIcon
        }))] : [];
    }, [tenantList]);

    const FilteredTenants: NavItemConfig[] = useMemo(() => {
        return search ? TENANTS.filter(tenant => tenant.label.toLowerCase().includes(search.toLowerCase())) : TENANTS;
    }, [TENANTS, search])

    useEffect(() => {
        if (!isDropdownOpen) setSearch("");
    }, [isDropdownOpen])

    useEffect(() => {
        if (!isSuperAdmin && tenantList.length && !selectedTenant.value) systemsStore.changeTenant(tenantList[0]);
    }, [tenantList, isSuperAdmin, selectedTenant]);

    return !isSuperAdmin ? <span className={styles.logo__name}>{selectedTenant.value != 0 ? selectedTenant.label : ""}</span> : <div className='tenant_dropdown-container'>
        <Dropdown
            trigger={["click"]}
            placement="bottomLeft"
            visible={isDropdownOpen}
            align={{ offset: isCollapsed ? [40, -5] : [100, 0] }}
            overlayClassName={(isSuperAdmin && !showNoSelection) ? "mobile-bottom-dropdown" : ""}
            getPopupContainer={() => document.body}
            onVisibleChange={setIsDropdownOpen}
            overlay={
                <div className={`custom-dropdown tenant-dropdown`}>
                    <p>Select a tenant</p>
                    <div className='search-bar'>
                        <DebounceInput debounceSearch={search} placeholder="Search tenant..." setDebouncedSearch={setSearch} />
                    </div>
                    {(loading || !FilteredTenants.length) ? <div className='empty-data'>{loading ? "Loading..." : "No Tenants Found"}</div> : <Menu>
                        {FilteredTenants.map((item) => (
                            <NavItem
                                key={item.key}
                                item={item}
                                isActive={`${selectedTenant.value}` === item.path}
                                isOpen={false}
                                hasChildren={false}
                                onClick={() => handleItemClick(item)}
                            />
                        ))}
                    </Menu>}
                </div>
            }>
            <NavItem {...{ item: { key: selectedTenant.label, label: selectedTenant.label, path: `${selectedTenant.value}`, icon: tenantIcon }, isActive: selectedTenant.value != 0, isOpen: false, hasChildren: true, onClick: () => null }} />
        </Dropdown>
        {showNoSelection && <div className='divider' style={selectedTenant.value !== 0 ? { marginBottom: "0px" } : {}}></div>}

        {selectedTenant.value === 0 && <div className='select-tenant' style={{ display: showNoSelection ? "block" : "none" }}>
            <NavItem {...{ item: {key:"no-tenant", label:"Select a tenant", icon:<Icon type='phishingManagerWarningModal'/> }, isActive: false, isOpen: false, hasChildren: false, onClick: () => null }} />
        </div>}
    </div>
}

export default observer(TenantSelection);